import { useFieldContext } from '@/components/form/formHooks';
import {
  Body,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@e2n/ui/v2';
import { faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ComponentProps, ReactNode } from 'react';

/** V2-Gegenstück zu `FieldBase` (Label, Info-Hinweis, Fehler/Warnungen). */
export function FieldBaseV2({
  children,
  label,
  isRequired = false,
  infoText,
  className,
  ...props
}: {
  children: ReactNode;
  label: string;
  isRequired?: boolean;
  infoText?: string;
  className?: string;
} & ComponentProps<'div'>) {
  const field = useFieldContext();
  const isInvalid = !field.state.meta.isValid;
  // Siehe `FieldBase`: `warnings` ist eine eigene Erweiterung des Field-Meta.
  const warnings = (
    field.state.meta as { warnings?: Array<{ message: string }> }
  ).warnings;
  const hasWarning = field.state.meta.isTouched && warnings;

  return (
    <Field data-invalid={isInvalid} className={className} {...props}>
      <FieldLabel
        htmlFor={field.name}
        invalid={isInvalid}
        requiredField={isRequired}
        infoButton={
          infoText && <InfoButton text={infoText} name={field.name} />
        }>
        {label}
      </FieldLabel>
      {children}
      {isInvalid ? (
        <FieldError errors={field.state.meta.errors} />
      ) : (
        hasWarning && <FieldError errors={warnings} variant="warning" />
      )}
    </Field>
  );
}

function InfoButton({ text, name }: { text: string; name: string }) {
  const icon = (
    <FontAwesomeIcon
      className="size-3.5 text-labels-secondary"
      icon={faCircleQuestion}
    />
  );

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          aria-label={`Info ${name}`}
          className="ml-1 hidden size-6 items-center justify-center md:inline-flex">
          {icon}
        </TooltipTrigger>
        <TooltipContent className="max-w-82.5">
          <Body>{text}</Body>
        </TooltipContent>
      </Tooltip>
      <div className="md:hidden">
        <Popover>
          <PopoverTrigger
            aria-label={`Info ${name}`}
            className="ml-1 inline-flex size-6 items-center justify-center">
            {icon}
          </PopoverTrigger>
          <PopoverContent className="p-4">
            <Body>{text}</Body>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}
